"use client";

/**
 * GaDashboard.tsx
 * ───────────────
 * Google Analytics–style dashboard rendered inside the "Dashboard" tab.
 * This tab only appears for datasets whose ID ends with "_ga" (e.g. bplus_ga).
 *
 * Layout (3 rows):
 *   Row 1 │ User activity over time (line chart)  │  Active users last 1 day (table)
 *   Row 2 │ Active users by OS (chart)             │  Latest app release overview (table)
 *   Row 3 │ Views by Page title & screen class (full-width table)
 *
 * DATA FLOW:
 *   - When duckLoaded=true, fires "GetGaDashboardData" via props.onEvent
 *   - Handler (MainInterface.ts) fetches 4 parquets from data/{datasetId}/main/
 *     and returns results via setState callbacks
 *   - While loading (or if no onEvent passed), mockup data is shown as fallback
 */

import { useEffect, useMemo, useState } from "react";
import type { UserActivityRow, CountryRow, OsRow, PageViewRow } from "./MainInterface";
import VegaEmbed from "@/components/VegaEmbed";
import "./GaDashboard.css";

// ─────────────────────────────────────────────
// SECTION 1 — MOCKUP DATA
// ─────────────────────────────────────────────
//
// All values below are hardcoded stand-ins until the Python pipeline
// produces real dashboard data.  Each constant is documented with the
// expected real-data shape so a future developer knows exactly what to
// swap in.

/**
 * DATES_30 — array of the last 30 calendar dates as "YYYY-MM-DD" strings.
 * Used as the X-axis for every time-series chart.
 *
 * Real data: generate the same date range from your data warehouse query.
 *
 * Example output:
 *   ["2026-02-27", "2026-02-28", ..., "2026-03-28"]  // 30 items
 */
const DATES_30 = (() => {
  const dates: string[] = [];
  const end = new Date("2026-03-28");
  for (let i = 29; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(d.getDate() - i);
    dates.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    );
  }
  return dates;
})();

/**
 * BASE_30 / BASE_7 / BASE_1
 * Daily active-user counts for three rolling windows.
 *
 * Index i corresponds to DATES_30[i].
 *
 * Real data shape (one row per date per window):
 *   SELECT event_date, COUNT(DISTINCT user_id) AS users
 *   FROM base_with_extracted_data
 *   WHERE event_date >= CURRENT_DATE - INTERVAL '30 days'
 *   GROUP BY event_date ORDER BY event_date
 */
const BASE_30 = [310,325,318,340,355,342,328,315,330,350,365,380,372,360,375,390,395,388,400,412,408,415,420,410,425,430,418,435,440,415];
const BASE_7  = [105, 98,112,108,115,120,110,102,118,125,130,122,115,128,132,138,135,128,142,148,145,150,155,148,152,158,145,160,165,198];
const BASE_1  = [ 45, 42, 48, 50, 47, 55, 52, 48, 58, 62, 56, 60, 58, 52, 65, 70, 68, 63, 72, 75, 70, 68, 80, 75, 72, 78, 68, 82, 85, 52];

/**
 * userActivityData — long-format array fed into the Vega-Lite line chart.
 *
 * Shape: { date: string, series: "30 days"|"7 days"|"1 day", users: number }[]
 *
 * Example rows:
 *   { date: "2026-03-28", series: "30 days", users: 415 }
 *   { date: "2026-03-28", series: "7 days",  users: 198 }
 *   { date: "2026-03-28", series: "1 day",   users: 52  }
 *
 * Vega-Lite needs ALL series in ONE flat array — it uses the "series" field
 * to split into separate coloured lines via `color` encoding.
 */
const userActivityData = [
  ...DATES_30.map((date, i) => ({ date, series: "30 days", users: BASE_30[i] })),
  ...DATES_30.map((date, i) => ({ date, series: "7 days",  users: BASE_7[i]  })),
  ...DATES_30.map((date, i) => ({ date, series: "1 day",   users: BASE_1[i]  })),
];

/**
 * topCountries — rows for the "Active users in last 1 day" table.
 *
 * Shape: { country: string, users: number }[]
 *
 * Real data:
 *   SELECT country, COUNT(DISTINCT user_id) AS users
 *   FROM base_with_extracted_data
 *   WHERE event_date = CURRENT_DATE - 1
 *   GROUP BY country ORDER BY users DESC LIMIT 10
 */
const topCountries = [
  { country: "Thailand",      users: 1245 },
  { country: "Japan",         users: 342  },
  { country: "United States", users: 218  },
  { country: "Singapore",     users: 156  },
  { country: "Vietnam",       users: 98   },
];

/**
 * OS_LIST / OS_COLORS / OS_SHAPES
 * Used together to render the "Active users by Operating System" chart.
 *
 * Web analytics does not have an "app version" concept.
 * Operating system is the closest equivalent — it shows the mobile vs desktop split.
 *
 * Real data column: `operating_system` in base_with_extracted_data.
 * Typical values from Firebase/GA4 web: "Android", "iOS", "Windows", "Macintosh", "Linux"
 */
const OS_LIST   = ["Android", "iOS", "Windows", "Macintosh", "Linux"];
const OS_COLORS = ["#4285f4", "#8bc34a", "#ff8f00", "#9e9e9e", "#e91e63"];
// Vega-Lite built-in shape names — one per OS so lines are readable without color:
const OS_SHAPES = ["circle", "square", "diamond", "triangle-down", "triangle-up"];

/**
 * osData — long-format array for the OS line chart.
 *
 * Shape: { date: string, os: string, users: number }[]
 *
 * Example rows:
 *   { date: "2026-03-01", os: "Android",   users: 120 }
 *   { date: "2026-03-01", os: "iOS",        users: 85  }
 *   { date: "2026-03-01", os: "Windows",    users: 40  }
 *
 * Real data SQL:
 *   SELECT event_date AS date,
 *          operating_system AS os,
 *          COUNT(DISTINCT user_id) AS users
 *   FROM base_with_extracted_data
 *   WHERE event_date >= STRFTIME(CURRENT_DATE - INTERVAL 30 DAYS, '%Y%m%d')
 *     AND operating_system IS NOT NULL
 *   GROUP BY date, os
 *   ORDER BY date
 */
const osData = OS_LIST.flatMap((os, oi) =>
  DATES_30.map((date, di) => ({
    date,
    os,
    // Mock: each OS has a different base count and oscillation pattern
    users: Math.max(0, [120, 85, 40, 25, 8][oi] + Math.round(Math.sin(di * 0.4 + oi * 1.3) * [20, 15, 10, 6, 3][oi])),
  }))
);

/**
 * appReleases — rows for the "Latest app release overview" table.
 *
 * Shape: { platform: "ios"|"android", name: string, version: string, status: string }[]
 *
 * Real data: this likely comes from your CI/CD system (e.g. Firebase App Distribution),
 * not from GA events.  For now it is static mockup.
 */
const appReleases = [
  { platform: "ios",     name: "mehealth",    version: "1.0.18", status: "Successful" },
  { platform: "android", name: "MehealthVc1", version: "1.0.22", status: "Successful" },
];

/**
 * pageViews — rows for the "Views by Page title and screen class" table.
 *
 * Shape: { page: string, views: number }[]
 *
 * Real data:
 *   SELECT COALESCE(page_title, page_location) AS page,
 *          COUNT(*) AS views
 *   FROM base_with_extracted_data
 *   GROUP BY page ORDER BY views DESC LIMIT 10
 */
const pageViews = [
  { page: "WebActivity",               views: 247 },
  { page: "MeHeyPro (20260323_1816…)", views: 213 },
  { page: "MainActivity",              views: 208 },
  { page: "MeHeyPro (20260312_1731…)", views: 157 },
  { page: "WebViewController",         views: 148 },
  { page: "MeHealth Studio",           views: 123 },
  { page: "MeHeyPro (20260318_2224…)", views: 87  },
];

// ─────────────────────────────────────────────
// SECTION 2 — VEGA-LITE CHART SPECS
// ─────────────────────────────────────────────
//
// A Vega-Lite "spec" is a JSON object that fully describes a chart.
// VegaEmbed renders it as an SVG/Canvas inside the browser.
//
// Key fields every spec has:
//   $schema  — which version of Vega-Lite to use (always v5 here)
//   width    — "container" means stretch to fill the parent div
//   height   — fixed pixel height
//   data     — { values: [...] } with the flat array of rows
//   mark     — chart type ("line", "bar", "point", …)
//   encoding — maps data fields to visual channels (x, y, color, shape, …)
//   config   — global style overrides

/**
 * Shared axis style — dark theme colours applied to every axis.
 * Centralised here so changing the theme only needs one edit.
 */
const AXIS_STYLE = {
  labelColor:  "#9ca3af", // grey labels on dark background
  gridColor:   "#2d3748", // subtle grid lines
  domainColor: "#374151", // axis spine
  tickColor:   "#374151", // tick marks
  titleColor:  "#9ca3af",
};

/**
 * userActivitySpec — Vega-Lite spec for the "User activity over time" chart.
 *
 * Accepts `data` so the same spec works for both mockup and real data.
 *
 * Encoding breakdown:
 *   x     → date field (temporal = Vega knows it's a date, formats it automatically)
 *   y     → users count (quantitative = continuous number)
 *   color → series field  ("30 days" / "7 days" / "1 day")
 *           The domain/range arrays map each series name to a specific hex color.
 *   legend: null → we draw our own custom legend panel on the right side
 */
function userActivitySpec(data: { date: string; series: string; users: number }[]) {
  return {
    $schema: "https://vega.github.io/schema/vega-lite/v5.json",
    width:   "container",
    height:  210,
    background: "transparent", // card background shows through
    data: { values: data },
    mark: {
      type:        "line",
      strokeWidth: 2,
      interpolate: "monotone", // smooth curves (not jagged)
    },
    encoding: {
      x: {
        field: "date",
        type:  "temporal",
        axis:  { ...AXIS_STYLE, format: "%d %b", title: null, tickCount: 8 },
      },
      y: {
        field:  "users",
        type:   "quantitative",
        axis:   { ...AXIS_STYLE, title: null, orient: "right", tickCount: 5 },
      },
      color: {
        field: "series",
        type:  "nominal",
        scale: {
          domain: ["30 days", "7 days", "1 day"],
          range:  ["#4285f4", "#8bc34a", "#ff8f00"],
        },
        legend: null, // hide Vega's default legend; we use <LegendItem> components
      },
    },
    config: { view: { stroke: null } }, // remove the default border around the plot area
  };
}

/**
 * osSpec — Vega-Lite LAYERED spec for the "Active users by Operating System" chart.
 *
 * Accepts `data` and `osList` so colors/shapes are derived dynamically from
 * the real OS values returned by the pipeline (not a static hardcoded list).
 *
 * Why layered?  We need two separate mark types on the same axes:
 *   Layer 1 (line)  — connects the dots to show the trend per OS over time
 *   Layer 2 (point) — draws a distinct marker shape at each data point
 *                     so the lines are still distinguishable without color alone
 */
function osSpec(
  data: { date: string; os: string; users: number }[],
  osList: string[],
) {
  // Derive colors and shapes from the actual OS list length
  const colors = ["#4285f4", "#8bc34a", "#ff8f00", "#9e9e9e", "#e91e63", "#00bcd4", "#9c27b0"];
  const shapes = ["circle", "square", "diamond", "triangle-down", "triangle-up", "cross", "triangle-right"];
  const colorRange = colors.slice(0, osList.length);
  const shapeRange = shapes.slice(0, osList.length);

  return {
    $schema:    "https://vega.github.io/schema/vega-lite/v5.json",
    width:      "container",
    height:     230,
    background: "transparent",
    data: { values: data },
    layer: [
      // ── Layer 1: lines ──
      {
        mark: { type: "line", strokeWidth: 1.5 },
        encoding: {
          x: {
            field: "date", type: "temporal",
            axis: { ...AXIS_STYLE, format: "%d %b", title: null, tickCount: 6 },
          },
          y: {
            field: "users", type: "quantitative",
            axis: { ...AXIS_STYLE, title: null, tickCount: 5 },
          },
          color: {
            field: "os", type: "nominal",
            scale:  { domain: osList, range: colorRange },
            // Show Vega's legend for this layer — it becomes the OS legend below the chart
            legend: { orient: "bottom", labelColor: "#9ca3af", labelFontSize: 11, symbolSize: 60 },
          },
        },
      },
      // ── Layer 2: points (shapes) ──
      {
        mark: { type: "point", filled: true, size: 30 },
        encoding: {
          x:     { field: "date",  type: "temporal"     },
          y:     { field: "users", type: "quantitative" },
          color: {
            field: "os", type: "nominal",
            scale:  { domain: osList, range: colorRange },
            legend: null, // already shown in layer 1
          },
          shape: {
            field: "os", type: "nominal",
            scale:  { domain: osList, range: shapeRange },
            legend: null,
          },
        },
      },
    ],
    config: { view: { stroke: null } },
  };
}

// ─────────────────────────────────────────────
// SECTION 3 — MAIN COMPONENT
// ─────────────────────────────────────────────

/**
 * GaDashboard — the top-level component rendered in the Dashboard tab.
 *
 * Props:
 *   datasetId  — e.g. "bplus_ga" — used to fetch the right parquet files
 *   chartInfo  — full chartInfo object from parent (reserved for future use)
 *   onEvent    — event dispatcher from MainController (fires GetGaDashboardData)
 *   duckLoaded — true once DuckDB-WASM is ready; gates all data fetching
 *
 * Behavior:
 *   - Before duckLoaded: renders mockup data immediately (no flash of empty state)
 *   - After duckLoaded:  fetches real data → swaps in results widget by widget
 */
export default function GaDashboard(props: {
  datasetId?: string;
  chartInfo?: any;
  onEvent?: (e: any) => Promise<unknown> | unknown;
  duckLoaded?: boolean;
}) {
  // ── State for real data (null = not yet fetched) ──────────────────────────
  const [loading, setLoading]             = useState(true);
  const [loadError, setLoadError]         = useState<string | null>(null);
  const [userActivity, setUserActivity]   = useState<UserActivityRow[] | null>(null);
  const [countriesReal, setCountriesReal] = useState<CountryRow[] | null>(null);
  const [osDataReal, setOsDataReal]       = useState<OsRow[] | null>(null);
  const [pageViewsReal, setPageViewsReal] = useState<PageViewRow[] | null>(null);

  // ── Fire the event once DuckDB is ready ──────────────────────────────────
  // Re-fires if the user switches to a different _ga dataset (datasetId changes)
  useEffect(() => {
    if (!props.duckLoaded) return;
    const onEvent = props.onEvent;
    const datasetId = props.datasetId;
    if (!onEvent || !datasetId) {
      setLoadError("Dashboard configuration is unavailable.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const loadDashboard = async () => {
      try {
        await onEvent({
          message: "GetGaDashboardData",
          params: {
            datasetId,
            setUserActivity,
            setTopCountries: setCountriesReal,
            setOsData: setOsDataReal,
            setPageViews: setPageViewsReal,
          },
        });
      } catch (error) {
        console.error("Failed to load GA dashboard data.", error);
        if (!cancelled) setLoadError("Could not load dashboard data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [props.duckLoaded, props.datasetId, props.onEvent]);

  // ── Derive display values: real data with mockup as fallback ─────────────

  // Widget 1 — convert daily rows → Vega long-format { date, series, users }
  // Real data provides the "30 days" series; mockup provides all 3 series
  const activityVegaData = useMemo(() => {
    if (!userActivity?.length) return userActivityData; // mockup (3 series)
    return userActivity.map(r => ({ date: r.date, series: "30 days", users: r.users }));
  }, [userActivity]);

  // Summary numbers shown in the legend panel
  const total30 = useMemo(() =>
    userActivity?.length ? userActivity[userActivity.length - 1].users : 415,
  [userActivity]);
  const total7 = 198; // 7-day window not yet in real data — keeps mockup value
  const total1 = useMemo(() =>
    countriesReal ? countriesReal.reduce((s, r) => s + r.users, 0) : 52,
  [countriesReal]);

  // Widget 2 — use real countries or fall back to mockup
  const countriesDisplay = countriesReal ?? topCountries;

  // Widget 3 — derive OS list dynamically from real data for Vega domain/range
  const osList = useMemo(() =>
    osDataReal?.length ? [...new Set(osDataReal.map(r => r.os))] : OS_LIST,
  [osDataReal]);
  const osVegaData = useMemo(() => {
    if (!osDataReal?.length) return osData; // mockup
    return osDataReal.map(r => ({ date: r.ym, os: r.os, users: r.users }));
  }, [osDataReal]);

  // Widget 4 — real page views or mockup
  const pageViewsDisplay = (pageViewsReal ?? pageViews) as { page: string; users: number }[];
  const maxViews = pageViewsDisplay[0]?.users ?? 1;

  // useMemo prevents rebuilding large spec objects on every render
  const activitySpec = useMemo(() => userActivitySpec(activityVegaData), [activityVegaData]);
  const versionSpec  = useMemo(() => osSpec(osVegaData, osList), [osVegaData, osList]);

  // ── Loading indicator (only shown after duckLoaded, while fetching) ───────
  if (loading && props.duckLoaded) {
    return (
      <div className="ga-dashboard">
        <div style={{ color: "#9ca3af", padding: "2rem", textAlign: "center" }}>
          Loading dashboard data…
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="ga-dashboard">
        <div role="alert" style={{ color: "#fca5a5", padding: "2rem", textAlign: "center" }}>
          {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="ga-dashboard">

      {/* ── ROW 1 ──────────────────────────────────────────────────────── */}
      {/* Left (2/3): line chart  |  Right (1/3): active-users counter     */}
      <div className="ga-row">

        {/* ── Card: User activity over time ── */}
        <div className="ga-card ga-col-2">
          <div className="ga-card-header">
            <span className="ga-card-title">User activity over time</span>
          </div>

          {/* Chart + custom legend side-by-side */}
          <div className="ga-activity-body">
            {/* VegaEmbed renders the Vega-Lite spec as an interactive SVG */}
            <div className="ga-chart-stretch">
              <VegaEmbed data={activitySpec} />
            </div>

            {/* Custom legend — shows coloured shape + label + total */}
            <div className="ga-legend-panel">
              <LegendItem color="#4285f4" shape="circle"  label="30 DAYS" value={total30} />
              <LegendItem color="#8bc34a" shape="square"  label="7 DAYS"  value={total7}  />
              <LegendItem color="#ff8f00" shape="diamond" label="1 DAY"   value={total1}  />
            </div>
          </div>
        </div>

        {/* ── Card: Active users in last 1 day ── */}
        <div className="ga-card ga-col-1">
          <p className="ga-metric-label">ACTIVE USERS IN LAST 1 DAY</p>

          {/* The big hero number */}
          <p className="ga-big-number">{total1}</p>

          <div className="ga-divider" />

          {/* Two-column table header */}
          <div className="ga-two-col-header">
            <span className="ga-col-label">TOP COUNTRIES</span>
            <span className="ga-col-label">ACTIVE USERS</span>
          </div>

          {/* One row per country */}
          {countriesDisplay.map((c, i) => (
            <div className="ga-two-col-row" key={i}>
              <span className="ga-cell">{c.country}</span>
              <span className="ga-cell ga-cell-right">{c.users.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── ROW 2 ──────────────────────────────────────────────────────── */}
      {/* Left (2/3): app-version chart  |  Right (1/3): release table     */}
      <div className="ga-row">

        {/* ── Card: Active users by App version ── */}
        <div className="ga-card ga-col-2">
          <div className="ga-card-header">
            <span className="ga-card-title">Active users by Operating System</span>
          </div>
          {/* Vega's own legend appears below the chart (orient: "bottom") */}
          <VegaEmbed data={versionSpec} />
        </div>

        {/* ── Card: Latest app release overview ── */}
        <div className="ga-card ga-col-1">
          <div className="ga-card-header">
            <span className="ga-card-title">Latest app release overview</span>
          </div>

          {/* Three-column table: APP | VERSION | STATUS */}
          <div className="ga-three-col-header" style={{ marginTop: 16 }}>
            <span className="ga-col-label ga-flex-2">APP</span>
            <span className="ga-col-label">VERSION</span>
            <span className="ga-col-label">STATUS</span>
          </div>

          {appReleases.map((r, i) => (
            <div className="ga-three-col-row" key={i}>
              <span className="ga-cell ga-flex-2 ga-app-cell">
                {/* PlatformBadge renders "iOS" badge or android emoji */}
                <PlatformBadge platform={r.platform} />
                {r.name}
              </span>
              <span className="ga-cell">{r.version}</span>
              {/* Green text for success status */}
              <span className="ga-cell ga-status-success">{r.status}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── ROW 3 ──────────────────────────────────────────────────────── */}
      {/* Full-width: page view counts with inline bar indicator            */}
      <div className="ga-row">
        <div className="ga-card ga-col-full">
          <div className="ga-card-header">
            <span className="ga-card-title">Views by Page title and screen class</span>
          </div>

          <div className="ga-two-col-header" style={{ marginTop: 16 }}>
            <span className="ga-col-label ga-flex-3">PAGE TITLE AND SCREEN …</span>
            <span className="ga-col-label">VIEWS</span>
          </div>

          {pageViewsDisplay.map((p, i) => (
            <div className="ga-views-row" key={i}>
              {/* Left cell: page name + proportional blue bar */}
              <div className="ga-flex-3">
                <span className="ga-cell">{p.page}</span>
                <div className="ga-bar-track">
                  {/* Bar width = this page's views / max views * 100% */}
                  <div className="ga-bar-fill" style={{ width: `${(p.users / maxViews) * 100}%` }} />
                </div>
              </div>
              <span className="ga-cell ga-cell-right">{p.users.toLocaleString()}</span>
            </div>
          ))}

          <div className="ga-card-footer">
            <span className="ga-link">View pages and screens →</span>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────
// SECTION 4 — SMALL SUB-COMPONENTS
// ─────────────────────────────────────────────

/**
 * LegendItem — one row in the custom chart legend.
 *
 * Shows:  [colored shape]  LABEL
 *                          999
 *
 * @param color  Hex color matching the chart line
 * @param shape  "circle" | "square" | "diamond"
 * @param label  e.g. "30 DAYS"
 * @param value  Total count displayed below the label
 */
function LegendItem({ color, shape, label, value }: {
  color: string;
  shape: string;
  label: string;
  value: number;
}) {
  return (
    <div className="ga-legend-item">
      <LegendShape color={color} shape={shape} />
      <div>
        <p className="ga-legend-label">{label}</p>
        <p className="ga-legend-value">{value.toLocaleString()}</p>
      </div>
    </div>
  );
}

/**
 * LegendShape — renders the small colored icon next to a legend label.
 * Circle = filled dot, Square = small square, Diamond = rotated square.
 */
function LegendShape({ color, shape }: { color: string; shape: string }) {
  if (shape === "circle") {
    return <span className="ga-shape-circle" style={{ background: color }} />;
  }
  if (shape === "square") {
    return <span className="ga-shape-square" style={{ background: color }} />;
  }
  // diamond — a square rotated 45° via CSS transform
  return (
    <span className="ga-shape-diamond-wrap">
      <span className="ga-shape-diamond" style={{ background: color }} />
    </span>
  );
}

/**
 * PlatformBadge — shows a small "iOS" blue badge or Android robot emoji
 * next to the app name in the release overview table.
 *
 * @param platform  "ios" | "android"
 */
function PlatformBadge({ platform }: { platform: string }) {
  if (platform === "ios") {
    return <span className="ga-badge ga-badge-ios">iOS</span>;
  }
  return <span className="ga-badge ga-badge-android">🤖</span>;
}
